import React from 'react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING } from '../../styles/emziCI';
import StructuredListInput from './StructuredListInput';
import { BILINGUAL_REQUIRED } from '../../utils/formValidation';

/**
 * EnhancedServicesForm - Services pack form with Input Quality Layer enhancements
 * Focus: Service offerings, procedures, customer-facing service information
 */
const EnhancedServicesForm = ({ 
  content = {}, 
  onChange, 
  errors = {}, 
  touched = {},
  onFieldBlur,
  packType = 'services'
}) => {
  const handleChange = (field, value) => {
    onChange({ ...content, [field]: value });
  };

  const requiresBilingual = BILINGUAL_REQUIRED[packType];

  return (
    <div style={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: EMZI_SPACING.md,
    }}>
      {/* Service Overview */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Service Overview
          <span style={{ color: EMZI_COLORS.error, marginLeft: '4px' }}>*</span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 Brief description of this service. Example: "An EMZI Care service offering health consultations and product refill support nationwide"
        </div>

        <textarea
          value={content.service_overview || ''}
          onChange={(e) => handleChange('service_overview', e.target.value)}
          onBlur={() => onFieldBlur && onFieldBlur('service_overview')}
          placeholder="e.g., An EMZI Care service offering health consultations, pharmacist-guided supplement plans, and product refill support nationwide"
          rows={3}
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${touched.service_overview && errors.content?.service_overview ? EMZI_COLORS.error : EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontFamily: 'inherit',
            outline: 'none',
            resize: 'vertical',
          }}
        />
        {touched.service_overview && errors.content?.service_overview && (
          <div style={{
            marginTop: '4px',
            fontSize: '12px',
            color: EMZI_COLORS.error,
          }}>
            {errors.content.service_overview}
          </div>
        )}
      </div>

      {/* Service Features */}
      <StructuredListInput
        label="Service Features"
        items={content.service_features || []}
        onChange={(items) => handleChange('service_features', items)}
        minItems={2}
        maxItems={10}
        placeholder="Add a service feature..."
        helper="Key features of this service. Example: 'Pharmacist-guided supplement plans' or 'Follow-up reminders' or 'Nationwide support'"
        required={true}
        hasError={touched.service_features && errors.content?.service_features}
        errorMessage={errors.content?.service_features}
      />

      {/* Service Benefits */}
      <StructuredListInput
        label="Customer Benefits"
        items={content.customer_benefits || []}
        onChange={(items) => handleChange('customer_benefits', items)}
        minItems={1}
        maxItems={8}
        placeholder="Add a customer benefit..."
        helper="How customers benefit. Example: 'Improves adherence and personalized health outcomes' or 'Convenient refill management with expert guidance'"
        required={false}
        hasError={false}
      />

      {/* Process Steps */}
      <StructuredListInput
        label="Process Steps"
        items={content.process_steps || []}
        onChange={(items) => handleChange('process_steps', items)}
        minItems={0}
        maxItems={15}
        placeholder="Add a process step..."
        helper="Step-by-step how this service works. Example: '1. Place order online' '2. Receive confirmation SMS' '3. Delivery within 24 hours'"
        required={false}
        hasError={false}
      />

      {/* Eligibility/Requirements */}
      <StructuredListInput
        label="Eligibility/Requirements"
        items={content.requirements || []}
        onChange={(items) => handleChange('requirements', items)}
        minItems={0}
        maxItems={10}
        placeholder="Add a requirement..."
        helper="What customers need to qualify. Example: 'Minimum order RM100' or 'Valid phone number required'"
        required={false}
        hasError={false}
      />

      {/* Pricing/Fees */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Pricing/Fees
          <span style={{
            fontSize: '11px',
            color: EMZI_COLORS.textMuted,
            marginLeft: EMZI_SPACING.xs,
            fontWeight: '400',
          }}>
            (Optional)
          </span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 Cost information. Example: "Free for orders above RM100, RM10 delivery fee otherwise"
        </div>

        <textarea
          value={content.pricing || ''}
          onChange={(e) => handleChange('pricing', e.target.value)}
          placeholder="e.g., Free for orders above RM100, RM10 delivery fee otherwise"
          rows={2}
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontFamily: 'inherit',
            outline: 'none',
            resize: 'vertical',
          }}
        />
      </div>

      {/* Limitations/Exclusions */}
      <StructuredListInput
        label="Limitations/Exclusions"
        items={content.limitations || []}
        onChange={(items) => handleChange('limitations', items)}
        minItems={0}
        maxItems={10}
        placeholder="Add a limitation..."
        helper="What's not included or restrictions. Example: 'Not available for remote areas' or 'Excludes bulky items'"
        required={false}
        hasError={false}
      />

      {/* Contact/Support */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Contact/Support
          <span style={{
            fontSize: '11px',
            color: EMZI_COLORS.textMuted,
            marginLeft: EMZI_SPACING.xs,
            fontWeight: '400',
          }}>
            (Optional)
          </span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 How to get help with this service. Example: "Call 1-800-XXX-XXXX or WhatsApp +60123456789"
        </div>

        <input
          type="text"
          value={content.contact_support || ''}
          onChange={(e) => handleChange('contact_support', e.target.value)}
          placeholder="e.g., Call 1-800-XXX-XXXX or WhatsApp +60123456789"
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            outline: 'none',
          }}
        />
      </div>

      {/* Completion hint */}
      <div style={{
        padding: EMZI_SPACING.sm,
        background: '#e6f7ed',
        borderRadius: EMZI_RADIUS.sm,
        fontSize: '12px',
        color: EMZI_COLORS.success,
        borderLeft: `3px solid ${EMZI_COLORS.success}`,
      }}>
        ✅ <strong>Pro Tip:</strong> Services packs help agents explain offerings clearly. Include process steps and eligibility to reduce confusion!
      </div>
    </div>
  );
};

export default EnhancedServicesForm;
